import { supabase } from "@/integrations/supabase/client";
import { familyInviteService } from "@/services/family";
import { analytics } from "@/lib/analytics";
import { SignInResponse } from "@/types/auth";

/**
 * Service for Google Sign-In authentication
 */
export const authGoogleService = {
  /**
   * Sign in with Google using OAuth
   */
  signInWithGoogle: async (inviteCode?: string): Promise<SignInResponse> => {
    try {
      console.log('🔵 Initiating Google OAuth...');
      
      const redirectUrl = 'https://forkast.in/';
      
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: redirectUrl,
          queryParams: inviteCode ? { invite_code: inviteCode } : undefined,
        }
      });

      if (error) {
        console.error('🔵 Google OAuth error:', error);
        throw error;
      }

      console.log('🔵 Google OAuth initiated successfully');
      
      analytics.track('user_signed_in', {
        signup_method: 'google',
        has_invite_code: !!inviteCode,
        oauth_initiated: true
      });

      return { session: null, error: null };
    } catch (error) {
      console.error('🔵 Google sign-in error:', error);
      analytics.trackAuthError('google_signin', error instanceof Error ? error.message : 'Unknown error');
      throw error;
    }
  },

  /**
   * Handle post-authentication tasks for Google users
   * This should be called after successful OAuth redirect
   */
  handleGoogleAuthCallback: async (session: any, inviteCode?: string) => {
    try {
      if (!session?.user) {
        throw new Error('No user session provided');
      }

      console.log('🔵 Handling Google auth callback for user:', session.user.id);

      // Handle family invite if provided
      if (inviteCode && session.user) {
        console.log('🔵 Processing family invite for Google user');
        try {
          await familyInviteService.joinFamilyByInviteCode(session.user.id, inviteCode);
          console.log('🔵 Successfully joined family with invite code');
        } catch (inviteError) {
          console.error('🔵 Error joining family:', inviteError);
          // Don't throw here - authentication was successful, just log the invite error
        }
      }

      // Set user for analytics
      analytics.setUser(session.user.id);

      // Track successful authentication
      analytics.track('user_signed_in', {
        signup_method: 'google',
        user_id: session.user.id,
        email: session.user.email,
        auth_provider: 'google',
        has_invite_code: !!inviteCode
      });

      console.log('🔵 Google auth callback handled successfully');
      return { success: true };
    } catch (error) {
      console.error('🔵 Error in Google auth callback:', error);
      analytics.trackAuthError('google_auth_callback', error instanceof Error ? error.message : 'Unknown error');
      throw error;
    }
  }
};